import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { FiFileText, FiBriefcase, FiStar, FiMail, FiUsers, FiLogOut, FiPlus, FiEdit2, FiTrash2, FiX, FiUpload, FiLayout } from 'react-icons/fi';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import CmsPage from './CmsPage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: BACKEND_URL, withCredentials: true });

const AdminDashboard = () => {
  const { user, loading, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [blogPosts, setBlogPosts] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [applications, setApplications] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [modalType, setModalType] = useState('');

  useEffect(() => {
    if (user) fetchStats();
  }, [user]);

  useEffect(() => {
    if (!user) return;
    if (activeTab === 'blog') fetchBlog();
    if (activeTab === 'jobs') fetchJobs();
    if (activeTab === 'testimonials') fetchTestimonials();
    if (activeTab === 'contacts') fetchContacts();
    if (activeTab === 'applications') fetchApplications();
    if (activeTab === 'subscribers') fetchSubscribers();
  }, [activeTab, user]);

  const fetchStats = async () => { try { const r = await api.get('/api/admin/stats'); setStats(r.data); } catch {} };
  const fetchBlog = async () => { try { const r = await api.get('/api/blog'); setBlogPosts(r.data); } catch {} };
  const fetchJobs = async () => { try { const r = await api.get('/api/jobs'); setJobs(r.data); } catch {} };
  const fetchTestimonials = async () => { try { const r = await api.get('/api/testimonials'); setTestimonials(r.data); } catch {} };
  const fetchContacts = async () => { try { const r = await api.get('/api/admin/contacts'); setContacts(r.data); } catch {} };
  const fetchApplications = async () => { try { const r = await api.get('/api/admin/applications'); setApplications(r.data); } catch {} };
  const fetchSubscribers = async () => { try { const r = await api.get('/api/admin/newsletter'); setSubscribers(r.data); } catch {} };

  const openModal = (type, item = null) => { setModalType(type); setEditItem(item); setModalOpen(true); };
  const closeModal = () => { setModalOpen(false); setEditItem(null); setModalType(''); };

  const handleDelete = async (type, id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      await api.delete(`/api/admin/${type}/${id}`);
      toast.success('Deleted successfully');
      if (type === 'blog') fetchBlog();
      if (type === 'jobs') fetchJobs();
      if (type === 'testimonials') fetchTestimonials();
      fetchStats();
    } catch { toast.error('Failed to delete'); }
  };

  if (loading) return <div className="min-h-screen bg-black flex items-center justify-center"><div className="text-white/40">Loading...</div></div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: FiFileText },
    { id: 'pages', label: 'Pages (CMS)', icon: FiLayout },
    { id: 'blog', label: 'Blog Posts', icon: FiFileText },
    { id: 'jobs', label: 'Job Listings', icon: FiBriefcase },
    { id: 'testimonials', label: 'Testimonials', icon: FiStar },
    { id: 'contacts', label: 'Contacts', icon: FiMail },
    { id: 'applications', label: 'Applications', icon: FiUsers },
    { id: 'subscribers', label: 'Newsletter', icon: FiMail },
  ];

  return (
    <div className="min-h-screen bg-black text-white" data-testid="admin-dashboard">
      {/* Admin Header */}
      <div className="bg-[#0a0a0a] border-b border-white/5 px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="font-display text-xl font-bold">ARVARON <span className="text-[#17B9BE]">Admin</span></h1>
          <p className="text-xs text-white/30">{user.email}</p>
        </div>
        <button onClick={logout} data-testid="admin-logout-button" className="flex items-center text-sm text-white/40 hover:text-white transition-colors">
          <FiLogOut className="mr-2" /> Logout
        </button>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <div className="w-56 min-h-[calc(100vh-72px)] bg-[#0a0a0a] border-r border-white/5 p-4">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`admin-tab-${tab.id}`}
              className={`w-full flex items-center px-4 py-3 rounded text-sm mb-1 transition-colors ${activeTab === tab.id ? 'bg-[#17B9BE]/10 text-[#17B9BE]' : 'text-white/40 hover:text-white hover:bg-white/5'}`}>
              <tab.icon className="mr-3" size={16} /> {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 p-8">
          {/* Dashboard Tab */}
          {activeTab === 'dashboard' && stats && (
            <div>
              <h2 className="font-display text-2xl font-bold mb-6">Dashboard Overview</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {[
                  { label: 'Blog Posts', value: stats.blog_posts, color: '#72EA43' },
                  { label: 'Job Listings', value: stats.jobs, color: '#17B9BE' },
                  { label: 'Testimonials', value: stats.testimonials, color: '#0999E1' },
                  { label: 'Contact Inquiries', value: stats.contacts, color: '#39D383' },
                  { label: 'Applications', value: stats.applications, color: '#0066CC' },
                  { label: 'Subscribers', value: stats.subscribers, color: '#72EA43' },
                ].map((s, i) => (
                  <div key={i} className="bg-[#0a0a0a] p-6 rounded border border-white/5" data-testid={`stat-card-${i}`}>
                    <div className="text-3xl font-bold mb-1" style={{ color: s.color }}>{s.value}</div>
                    <div className="text-xs text-white/40 uppercase tracking-wider">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pages CMS Tab */}
          {activeTab === 'pages' && <CmsPage />}

          {/* Blog Tab */}
          {activeTab === 'blog' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-display text-2xl font-bold">Blog Posts</h2>
                <button onClick={() => openModal('blog')} data-testid="add-blog-button"
                  className="flex items-center px-4 py-2 brand-gradient-bg text-black rounded-sm text-sm font-semibold hover:opacity-90">
                  <FiPlus className="mr-2" /> New Post
                </button>
              </div>
              <div className="space-y-3">
                {blogPosts.map(post => (
                  <div key={post.id} className="bg-[#0a0a0a] p-5 rounded border border-white/5 flex items-center justify-between" data-testid={`blog-item-${post.id}`}>
                    <div className="flex-1">
                      <h3 className="font-semibold mb-1">{post.title}</h3>
                      <p className="text-xs text-white/30">{post.category} &middot; {post.author}</p>
                    </div>
                    <div className="flex space-x-2">
                      <button onClick={() => openModal('blog', post)} className="p-2 text-white/30 hover:text-[#17B9BE] transition-colors"><FiEdit2 size={16} /></button>
                      <button onClick={() => handleDelete('blog', post.id)} className="p-2 text-white/30 hover:text-red-400 transition-colors"><FiTrash2 size={16} /></button>
                    </div>
                  </div>
                ))}
                {blogPosts.length === 0 && <p className="text-white/30 text-sm">No blog posts yet.</p>}
              </div>
            </div>
          )}

          {/* Jobs Tab */}
          {activeTab === 'jobs' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-display text-2xl font-bold">Job Listings</h2>
                <button onClick={() => openModal('jobs')} data-testid="add-job-button"
                  className="flex items-center px-4 py-2 brand-gradient-bg text-black rounded-sm text-sm font-semibold hover:opacity-90">
                  <FiPlus className="mr-2" /> New Job
                </button>
              </div>
              <div className="space-y-3">
                {jobs.map(job => (
                  <div key={job.id} className="bg-[#0a0a0a] p-5 rounded border border-white/5 flex items-center justify-between" data-testid={`job-item-${job.id}`}>
                    <div className="flex-1">
                      <h3 className="font-semibold mb-1">{job.title}</h3>
                      <p className="text-xs text-white/30">{job.department} &middot; {job.location} &middot; {job.type}</p>
                    </div>
                    <div className="flex space-x-2">
                      <button onClick={() => openModal('jobs', job)} className="p-2 text-white/30 hover:text-[#17B9BE] transition-colors"><FiEdit2 size={16} /></button>
                      <button onClick={() => handleDelete('jobs', job.id)} className="p-2 text-white/30 hover:text-red-400 transition-colors"><FiTrash2 size={16} /></button>
                    </div>
                  </div>
                ))}
                {jobs.length === 0 && <p className="text-white/30 text-sm">No job listings yet.</p>}
              </div>
            </div>
          )}

          {/* Testimonials Tab */}
          {activeTab === 'testimonials' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-display text-2xl font-bold">Testimonials</h2>
                <button onClick={() => openModal('testimonials')} data-testid="add-testimonial-button"
                  className="flex items-center px-4 py-2 brand-gradient-bg text-black rounded-sm text-sm font-semibold hover:opacity-90">
                  <FiPlus className="mr-2" /> New Testimonial
                </button>
              </div>
              <div className="space-y-3">
                {testimonials.map(t => (
                  <div key={t.id} className="bg-[#0a0a0a] p-5 rounded border border-white/5 flex items-center justify-between" data-testid={`testimonial-item-${t.id}`}>
                    <div className="flex-1">
                      <h3 className="font-semibold mb-1">{t.name}</h3>
                      <p className="text-xs text-white/30">{t.position}, {t.company}</p>
                    </div>
                    <div className="flex space-x-2">
                      <button onClick={() => openModal('testimonials', t)} className="p-2 text-white/30 hover:text-[#17B9BE] transition-colors"><FiEdit2 size={16} /></button>
                      <button onClick={() => handleDelete('testimonials', t.id)} className="p-2 text-white/30 hover:text-red-400 transition-colors"><FiTrash2 size={16} /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Contacts Tab */}
          {activeTab === 'contacts' && (
            <div>
              <h2 className="font-display text-2xl font-bold mb-6">Contact Inquiries</h2>
              <div className="space-y-3">
                {contacts.map((c, i) => (
                  <div key={i} className="bg-[#0a0a0a] p-5 rounded border border-white/5">
                    <div className="flex justify-between mb-2">
                      <h3 className="font-semibold">{c.name}</h3>
                      <span className="text-xs text-white/30">{c.email}</span>
                    </div>
                    {c.company && <p className="text-xs text-white/40 mb-2">{c.company}</p>}
                    <p className="text-sm text-white/50">{c.message}</p>
                  </div>
                ))}
                {contacts.length === 0 && <p className="text-white/30 text-sm">No contact inquiries yet.</p>}
              </div>
            </div>
          )}

          {/* Applications Tab */}
          {activeTab === 'applications' && (
            <div>
              <h2 className="font-display text-2xl font-bold mb-6">Career Applications</h2>
              <div className="space-y-3">
                {applications.map((a, i) => (
                  <div key={i} className="bg-[#0a0a0a] p-5 rounded border border-white/5" data-testid={`application-item-${i}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold">{a.name}</h3>
                        <p className="text-xs text-white/40 mt-1">{a.email} &middot; {a.phone}</p>
                        {a.position && <p className="text-xs text-white/50 mt-1">Position: {a.position}</p>}
                      </div>
                      <select
                        value={a.status || 'New'}
                        onChange={async (e) => {
                          const newStatus = e.target.value;
                          try {
                            await api.put(`/api/admin/applications/${a.id}/status`, { status: newStatus });
                            toast.success(`Status updated to ${newStatus}`);
                            fetchApplications();
                          } catch { toast.error('Failed to update status'); }
                        }}
                        data-testid={`application-status-${i}`}
                        className={`text-xs px-3 py-1.5 rounded border transition-colors cursor-pointer ${
                          {
                            'New': 'bg-blue-500/10 border-blue-500/30 text-blue-400',
                            'Reviewed': 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400',
                            'Shortlisted': 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400',
                            'Interview': 'bg-purple-500/10 border-purple-500/30 text-purple-400',
                            'Offered': 'bg-[#17B9BE]/10 border-[#17B9BE]/30 text-[#17B9BE]',
                            'Rejected': 'bg-red-500/10 border-red-500/30 text-red-400',
                          }[a.status || 'New'] || 'bg-white/5 border-white/10 text-white/50'
                        }`}
                      >
                        <option value="New">New</option>
                        <option value="Reviewed">Reviewed</option>
                        <option value="Shortlisted">Shortlisted</option>
                        <option value="Interview">Interview</option>
                        <option value="Offered">Offered</option>
                        <option value="Rejected">Rejected</option>
                      </select>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-white/30">
                      {a.linkedin_profile && <a href={a.linkedin_profile} target="_blank" rel="noopener noreferrer" className="text-[#17B9BE] hover:underline">LinkedIn</a>}
                      {a.resume_filename && <span>Resume: {a.resume_filename}</span>}
                      {a.applied_at && <span>Applied: {new Date(a.applied_at).toLocaleDateString()}</span>}
                    </div>
                  </div>
                ))}
                {applications.length === 0 && <p className="text-white/30 text-sm">No applications yet.</p>}
              </div>
            </div>
          )}

          {/* Subscribers Tab */}
          {activeTab === 'subscribers' && (
            <div>
              <h2 className="font-display text-2xl font-bold mb-6">Newsletter Subscribers</h2>
              <div className="space-y-2">
                {subscribers.map((s, i) => (
                  <div key={i} className="bg-[#0a0a0a] p-4 rounded border border-white/5 flex justify-between items-center">
                    <span className="text-sm">{s.email}</span>
                    <span className="text-xs text-white/30">{s.subscribed_at ? new Date(s.subscribed_at).toLocaleDateString() : ''}</span>
                  </div>
                ))}
                {subscribers.length === 0 && <p className="text-white/30 text-sm">No subscribers yet.</p>}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {modalOpen && (
        <FormModal type={modalType} item={editItem} onClose={closeModal} onSave={() => {
          closeModal();
          fetchStats();
          if (modalType === 'blog') fetchBlog();
          if (modalType === 'jobs') fetchJobs();
          if (modalType === 'testimonials') fetchTestimonials();
        }} />
      )}
    </div>
  );
};

const FormModal = ({ type, item, onClose, onSave }) => {
  const isEdit = !!item;
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (type === 'blog') {
      setFormData(item || { title: '', slug: '', excerpt: '', content: '', author: '', category: '', image_url: '' });
    } else if (type === 'jobs') {
      setFormData(item ? { ...item, requirements: item.requirements?.join('\n') || '' } : { title: '', department: '', location: '', type: 'Full-time', description: '', requirements: '' });
    } else if (type === 'testimonials') {
      setFormData(item || { name: '', company: '', position: '', content: '', rating: 5 });
    }
  }, [type, item]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleContentChange = (value) => {
    setFormData(prev => ({ ...prev, content: value }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image too large. Max 5MB.');
      return;
    }
    setUploading(true);
    const uploadData = new FormData();
    uploadData.append('image', file);
    try {
      const res = await api.post('/api/admin/upload-image', uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData(prev => ({ ...prev, image_url: res.data.image_url }));
      toast.success('Image uploaded!');
    } catch (err) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let payload = { ...formData };
      if (type === 'jobs' && typeof payload.requirements === 'string') {
        payload.requirements = payload.requirements.split('\n').filter(r => r.trim());
      }
      if (type === 'testimonials') {
        payload.rating = parseInt(payload.rating) || 5;
      }

      if (isEdit) {
        await api.put(`/api/admin/${type}/${item.id}`, payload);
        toast.success('Updated successfully');
      } else {
        await api.post(`/api/admin/${type}`, payload);
        toast.success('Created successfully');
      }
      onSave();
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      ['blockquote', 'link'],
      [{ 'align': [] }],
      ['clean']
    ]
  };

  const inputClass = "w-full px-4 py-3 bg-white/5 border border-white/10 rounded text-white placeholder-white/25 focus:outline-none focus:border-[#17B9BE]/50 transition-colors text-sm";

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="admin-modal">
      <div className="bg-[#111] rounded border border-white/10 w-full max-w-2xl max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-white/5">
          <h3 className="font-display text-xl font-bold">{isEdit ? 'Edit' : 'Create'} {type === 'blog' ? 'Blog Post' : type === 'jobs' ? 'Job Listing' : 'Testimonial'}</h3>
          <button onClick={onClose} className="text-white/30 hover:text-white"><FiX size={20} /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {type === 'blog' && (
            <>
              <div><label className="block text-xs uppercase tracking-wider text-white/40 mb-1">Title</label><input name="title" value={formData.title || ''} onChange={handleChange} className={inputClass} required /></div>
              <div><label className="block text-xs uppercase tracking-wider text-white/40 mb-1">Slug</label><input name="slug" value={formData.slug || ''} onChange={handleChange} className={inputClass} required placeholder="url-friendly-slug" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-xs uppercase tracking-wider text-white/40 mb-1">Author</label><input name="author" value={formData.author || ''} onChange={handleChange} className={inputClass} required /></div>
                <div><label className="block text-xs uppercase tracking-wider text-white/40 mb-1">Category</label><input name="category" value={formData.category || ''} onChange={handleChange} className={inputClass} required /></div>
              </div>

              {/* Image Upload */}
              <div>
                <label className="block text-xs uppercase tracking-wider text-white/40 mb-1">Cover Image</label>
                <div className="flex items-center gap-3">
                  <input name="image_url" value={formData.image_url || ''} onChange={handleChange} className={`${inputClass} flex-1`} placeholder="Image URL or upload" required />
                  <label className={`flex-shrink-0 px-4 py-3 rounded cursor-pointer text-sm font-medium flex items-center gap-2 transition-colors ${uploading ? 'bg-white/10 text-white/30' : 'bg-[#17B9BE]/20 text-[#17B9BE] hover:bg-[#17B9BE]/30'}`}>
                    <FiUpload size={14} />
                    {uploading ? 'Uploading...' : 'Upload'}
                    <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" disabled={uploading} />
                  </label>
                </div>
                {formData.image_url && (
                  <div className="mt-2 relative h-32 rounded overflow-hidden border border-white/10">
                    <img src={formData.image_url.startsWith('/api') ? `${BACKEND_URL}${formData.image_url}` : formData.image_url} alt="Preview" className="w-full h-full object-cover" />
                  </div>
                )}
              </div>

              <div><label className="block text-xs uppercase tracking-wider text-white/40 mb-1">Excerpt</label><textarea name="excerpt" value={formData.excerpt || ''} onChange={handleChange} className={inputClass} rows="2" required /></div>

              {/* Rich Text Editor */}
              <div>
                <label className="block text-xs uppercase tracking-wider text-white/40 mb-1">Content</label>
                <div className="admin-quill-wrapper">
                  <ReactQuill
                    theme="snow"
                    value={formData.content || ''}
                    onChange={handleContentChange}
                    modules={quillModules}
                    placeholder="Write your blog post content..."
                  />
                </div>
              </div>
            </>
          )}

          {type === 'jobs' && (
            <>
              <div><label className="block text-xs uppercase tracking-wider text-white/40 mb-1">Title</label><input name="title" value={formData.title || ''} onChange={handleChange} className={inputClass} required /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-xs uppercase tracking-wider text-white/40 mb-1">Department</label><input name="department" value={formData.department || ''} onChange={handleChange} className={inputClass} required /></div>
                <div><label className="block text-xs uppercase tracking-wider text-white/40 mb-1">Location</label><input name="location" value={formData.location || ''} onChange={handleChange} className={inputClass} required /></div>
              </div>
              <div><label className="block text-xs uppercase tracking-wider text-white/40 mb-1">Type</label>
                <select name="type" value={formData.type || 'Full-time'} onChange={handleChange} className={inputClass}>
                  <option value="Full-time">Full-time</option><option value="Part-time">Part-time</option><option value="Contract">Contract</option><option value="Internship">Internship</option>
                </select>
              </div>
              <div><label className="block text-xs uppercase tracking-wider text-white/40 mb-1">Description</label><textarea name="description" value={formData.description || ''} onChange={handleChange} className={inputClass} rows="4" required /></div>
              <div><label className="block text-xs uppercase tracking-wider text-white/40 mb-1">Requirements (one per line)</label><textarea name="requirements" value={formData.requirements || ''} onChange={handleChange} className={inputClass} rows="4" required /></div>
            </>
          )}

          {type === 'testimonials' && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-xs uppercase tracking-wider text-white/40 mb-1">Name</label><input name="name" value={formData.name || ''} onChange={handleChange} className={inputClass} required /></div>
                <div><label className="block text-xs uppercase tracking-wider text-white/40 mb-1">Company</label><input name="company" value={formData.company || ''} onChange={handleChange} className={inputClass} required /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-xs uppercase tracking-wider text-white/40 mb-1">Position</label><input name="position" value={formData.position || ''} onChange={handleChange} className={inputClass} required /></div>
                <div><label className="block text-xs uppercase tracking-wider text-white/40 mb-1">Rating (1-5)</label><input name="rating" type="number" min="1" max="5" value={formData.rating || 5} onChange={handleChange} className={inputClass} required /></div>
              </div>
              <div><label className="block text-xs uppercase tracking-wider text-white/40 mb-1">Testimonial</label><textarea name="content" value={formData.content || ''} onChange={handleChange} className={inputClass} rows="4" required /></div>
            </>
          )}

          <button type="submit" disabled={saving} data-testid="admin-save-button"
            className="w-full px-8 py-4 brand-gradient-bg text-black font-semibold rounded-sm hover:opacity-90 transition-all disabled:opacity-50 text-sm uppercase tracking-wider">
            {saving ? 'Saving...' : (isEdit ? 'Update' : 'Create')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdminDashboard;
